import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { Text } from '@atlaskit/primitives/text';

import { Show } from '@atlaskit/primitives/responsive/show';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--search-plex/quick-find-spotlight';

import { type ComponentType, useState } from 'react';

import { FormattedMessage, defineMessages } from 'react-intl';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

const messages = defineMessages({
	spotlightHeading1: {
		id: 'post-office.quick-find-spotlight.spotlight-heading-1',
		defaultMessage: 'Filter for what you need',
		description: 'Heading for quick find spotlight first step.',
	},
	spotlightBody1: {
		id: 'post-office.quick-find-spotlight.spotlight-body-1',
		defaultMessage: 'Apply a mix of filters to narrow down results.',
		description: 'Description for quick find spotlight first step.',
	},
	spotlightButtonNext: {
		id: 'post-office.quick-find-spotlight.spotlight-button-next',
		defaultMessage: 'Next',
		description: 'Button to navigate to the next step in the spotlight tour.',
	},
	spotlightHeading2: {
		id: 'post-office.quick-find-spotlight.spotlight-heading-2',
		defaultMessage: 'Choose where to search',
		description: 'Heading for quick find spotlight second step.',
	},
	spotlightBody2: {
		id: 'post-office.quick-find-spotlight.spotlight-body-2',
		defaultMessage: 'Search only Jira, or expand across all your Atlassian and connected apps.',
		description: 'Description for quick find spotlight second step.',
	},
	spotlightButtonDone: {
		id: 'post-office.quick-find-spotlight.spotlight-button-done',
		defaultMessage: 'Done',
		description: 'Button to complete the spotlight tour.',
	},
});

interface FirstStepProps {
	dismiss: () => void;
	next: () => void;
}

interface LastStepProps {
	dismiss: () => void;
	done: () => void;
}

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<PlacementRenderProps>(),
		Component: ({ onDismiss }: PlacementRenderProps) => {
			const { onAcknowledged, onClosed, onStepChanged } = usePostOfficeUserInteractions();
			const { messageDismissed } = useMessageEvent();

			/**
			 * This uses `0` to mean 'the tour is not in progress',
			 * which eliminates the need for some type-checking in the message,
			 * and keeps array indexes consistent with tour step numbers.
			 */
			const [step, setStep] = useState<number>(1);
			const renderInline = useMountToHtmlElement({
				selectors: `#spotlight-multi-step-on-trigger-step-${step}`,
				as: 'inline',
				position: 'inside',
			});

			/** Ends the tour and removes the spotlight. */
			const closeTour = async () => {
				await messageDismissed();
				onDismiss();
			};

			const dismiss = async () => {
				await messageDismissed();
				void onClosed({ interactionIdentifier: 'dismiss' });

				onDismiss();
			};

			/**
			 * The tour's finish control, split out of `dismiss` so finishing the tour is no longer
			 * indistinguishable from closing it.
			 */
			const done = async () => {
				void onAcknowledged({ interactionIdentifier: 'done' });
				await closeTour();
			};

			const next = async () => {
				// Step numbers keep the legacy mapping: `fromStep = step - 1`, `toStep = step`.
				void onStepChanged({
					interactionIdentifier: 'next',
					direction: 'next',
					fromStep: step - 1,
					toStep: step,
				});
				setStep(Math.min(tour.length, step + 1));
			};

			const Step = tour[step] || (() => <></>);

			return <div>{renderInline(<Step dismiss={dismiss} next={next} done={done} />)}</div>;
		},
	},
});

const SpotlightCardOne = ({ dismiss, next }: FirstStepProps) => {
	return (
		<SpotlightCard>
			<SpotlightHeader>
				<SpotlightHeadline>
					<FormattedMessage {...messages.spotlightHeading1} />
				</SpotlightHeadline>
				<SpotlightControls>
					<SpotlightDismissControl onClick={dismiss} />
				</SpotlightControls>
			</SpotlightHeader>
			<SpotlightBody>
				<Text>
					<FormattedMessage {...messages.spotlightBody1} />
				</Text>
			</SpotlightBody>
			<SpotlightFooter>
				<SpotlightActions>
					<SpotlightPrimaryAction onClick={next}>
						<FormattedMessage {...messages.spotlightButtonNext} />
					</SpotlightPrimaryAction>
				</SpotlightActions>
			</SpotlightFooter>
		</SpotlightCard>
	);
};

const SpotlightCardTwo = ({ dismiss, done }: LastStepProps) => {
	return (
		<SpotlightCard>
			<SpotlightHeader>
				<SpotlightHeadline>
					<FormattedMessage {...messages.spotlightHeading2} />
				</SpotlightHeadline>
				<SpotlightControls>
					<SpotlightDismissControl onClick={dismiss} />
				</SpotlightControls>
			</SpotlightHeader>
			<SpotlightBody>
				<Text>
					<FormattedMessage {...messages.spotlightBody2} />
				</Text>
			</SpotlightBody>
			<SpotlightFooter>
				<SpotlightActions>
					<SpotlightPrimaryAction onClick={done}>
						<FormattedMessage {...messages.spotlightButtonDone} />
					</SpotlightPrimaryAction>
				</SpotlightActions>
			</SpotlightFooter>
		</SpotlightCard>
	);
};

const tour: (ComponentType<FirstStepProps> | ComponentType<LastStepProps>)[] = [
	// Tour not in progress
	() => <></>,

	// Step 1
	({ dismiss, next }: FirstStepProps) => {
		return (
			<PopoverProvider>
				<PopoverTarget>
					{/* Dummy target */}
					<div />
				</PopoverTarget>
				<PopoverContent dismiss={dismiss} placement="bottom-end" isVisible={true}>
					<SpotlightCardOne dismiss={dismiss} next={next} />
				</PopoverContent>
			</PopoverProvider>
		);
	},

	// Step 2
	({ dismiss, done }: LastStepProps) => {
		return (
			<>
				<Show above="lg">
					<PopoverProvider>
						<PopoverTarget>
							{/* Dummy target */}
							<div />
						</PopoverTarget>
						<PopoverContent dismiss={dismiss} placement="right-end" isVisible={true}>
							<SpotlightCardTwo dismiss={dismiss} done={done} />
						</PopoverContent>
					</PopoverProvider>
				</Show>
				<Show below="lg">
					<PopoverProvider>
						<PopoverTarget>
							{/* Dummy target */}
							<div />
						</PopoverTarget>
						<PopoverContent dismiss={dismiss} placement="bottom-start" isVisible={true}>
							<SpotlightCardTwo dismiss={dismiss} done={done} />
						</PopoverContent>
					</PopoverProvider>
				</Show>
			</>
		);
	},
];
